// src/Dealer/DealerSettings.jsx
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import DealerNav from "./DealerNav";
const API = process.env.REACT_APP_API_BASE;

export default function DealerSettings() {
  const navigate = useNavigate();
  const token = useMemo(() => localStorage.getItem("dealerToken"), []);
  const [slaHours, setSlaHours] = useState(24);
  const [alertEmail, setAlertEmail] = useState("");
  const [notifyNewLead, setNotifyNewLead] = useState(true);
  const [notifyOverdue, setNotifyOverdue] = useState(true);

  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/dealer/settings`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r.status === 401) return navigate("/dealer/login", { replace: true });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const s = await r.json();
        setSlaHours(Number(s?.slaHours ?? 24));
        setAlertEmail(s?.alertEmail ?? "");
        // defaults true if absent
        setNotifyNewLead(s?.notifyNewLead ?? true);
        setNotifyOverdue(s?.notifyOverdue ?? true);
      } catch {
        // keep defaults; optionally surface error
      }
    })();
  }, [API, token, navigate]);

  const save = async () => {
    setErr("");
    setMsg("");
    setSaving(true);
    const hours = Math.min(168, Math.max(1, Number(slaHours) || 24));

    try {
      const r = await fetch(`${API}/dealer/settings`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          slaHours: hours,
          alertEmail: (alertEmail || "").trim(),
          notifyNewLead,
          notifyOverdue,
        }),
      });

      if (r.status === 401) {
        navigate("/dealer/login", { replace: true });
        return;
      }

      if (!r.ok) {
        // Try to read API error for invalid email (422) etc.
        let msg = "Could not save settings.";
        try {
          const j = await r.json();
          if (j?.error) msg = j.error;
          if (j?.field === "alertEmail") msg = "Invalid email address.";
        } catch {}
        throw new Error(msg);
      }

      setMsg("Saved");
      setTimeout(() => setMsg(""), 1500);
    } catch (e) {
      setErr(e.message || "Could not save settings.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-4">
      <DealerNav />
      <h1 className="text-2xl font-semibold mb-4">Dealer Settings</h1>

      {err && (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {err}
        </div>
      )}

      <div className="bg-white border rounded-xl p-4 space-y-5">
        <div>
          <label className="block text-sm text-gray-600 mb-1">SLA Hours</label>
          <input
            type="number"
            min="1"
            max="168"
            value={slaHours}
            onChange={(e) => setSlaHours(e.target.value)}
            className="h-10 px-3 rounded-lg border border-gray-300 w-40"
          />
          <div className="text-xs text-gray-500 mt-1">
            Used by the “Overdue Leads” KPI and filters.
          </div>
        </div>

        <div>
          <label className="block text-sm text-gray-600 mb-1">Alert Email</label>
          <input
            type="email"
            value={alertEmail}
            onChange={(e) => setAlertEmail(e.target.value)}
            placeholder="alerts@yourdealership.com"
            className="h-10 px-3 rounded-lg border border-gray-300 w-full max-w-md"
          />
          <div className="text-xs text-gray-500 mt-1">
            Where to send email notifications (leave blank to disable).
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={notifyNewLead}
              onChange={(e) => setNotifyNewLead(e.target.checked)}
            />
            Email me for <span className="font-medium">New Leads</span>
          </label>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={notifyOverdue}
              onChange={(e) => setNotifyOverdue(e.target.checked)}
            />
            Email me for <span className="font-medium">Overdue Leads</span>
          </label>
        </div>

        <div className="flex gap-2">
          <button
            onClick={save}
            disabled={saving}
            className="h-10 px-4 rounded-lg bg-blue-600 text-white disabled:opacity-60"
          >
            {saving ? "Saving…" : "Save"}
          </button>
          {msg && <span className="text-green-700 text-sm">{msg}</span>}
        </div>
      </div>
    </div>
  );
}
